// src/components/messaging/ChatWindow.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Conversation, Message } from '../../types/message';
import { useMessages } from '../../hooks/useMessages';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useAuth } from '../../hooks/useAuth';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import Avatar from '../common/Avatar';

interface ChatWindowProps {
  activeConversation: Conversation | null;
  messages: Message[];
  loading: boolean;
  onNavigate: (page: string) => void;
  onBack?: () => void;
  sendMessage?: (messageInput: { content: string; type: 'text' }, conversationId: string) => Promise<{ success: boolean; error?: string }>;
  markAsRead?: (conversationId: string, messageIds: string[]) => Promise<{ success: boolean; error?: string }>;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  activeConversation,
  messages,
  loading,
  onNavigate,
  onBack,
  sendMessage: sendMessageProp,
  markAsRead: markAsReadProp
}) => {
  // Use props if provided, otherwise fall back to hook (for backward compatibility)
  const messagesHook = useMessages();
  const sendMessage = sendMessageProp || messagesHook.sendMessage;
  const markAsRead = markAsReadProp || messagesHook.markAsRead;
  const { isConnected, sendTypingIndicator, onMessage, offMessage } = useWebSocket();
  const { user } = useAuth();
  const [isTyping, setIsTyping] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const typingStopTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Phase 1: Track processed messages to prevent infinite loop
  const markedAsReadRef = useRef<Set<string>>(new Set());
  const lastProcessedLengthRef = useRef<number>(0);
  const lastConversationIdRef = useRef<string | null>(null);

  // Get current user ID for message alignment
  const currentUserId = user?.id || '';

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Phase 1: Mark messages as read when conversation changes or new messages arrive
  // Fixed to prevent infinite loop by tracking processed messages and using messages.length instead of messages array
  useEffect(() => {
    if (activeConversation && messages.length > 0 && currentUserId) {
      // Only process if messages array length changed (new messages arrived)
      // OR if conversation changed (don't process on every messages state update)
      const hasNewMessages = messages.length !== lastProcessedLengthRef.current;
      const conversationChanged = activeConversation.id !== lastConversationIdRef.current;
      
      if (hasNewMessages || conversationChanged) {
        lastProcessedLengthRef.current = messages.length;
        lastConversationIdRef.current = activeConversation.id;
        
        // Reset tracking when conversation changes
        if (conversationChanged) {
          markedAsReadRef.current.clear();
        }
        
        const unreadMessages = messages.filter(msg => {
          // Skip if already marked in this session
          if (markedAsReadRef.current.has(msg.id)) return false;
          
          const msgSenderId = msg.senderId?.toString() || msg.senderId;
          const userId = currentUserId.toString();
          return msgSenderId !== userId && msgSenderId !== 'current-user' && !msg.status.read;
        });
        
        if (unreadMessages.length > 0) {
          // Mark as processing immediately to prevent re-processing
          unreadMessages.forEach(msg => markedAsReadRef.current.add(msg.id));
          markAsRead(activeConversation.id, unreadMessages.map(msg => msg.id));
        }
      }
    }
  }, [activeConversation, messages, markAsRead, currentUserId]); // Include full objects to satisfy ESLint, refs prevent infinite loops

  // Listen for typing:update events from other users
  useEffect(() => {
    if (!isConnected || !activeConversation) return;

    const handleTypingUpdate = (data: any) => {
      // Only handle typing updates for the active conversation
      if (data.conversationId === activeConversation.id) {
        // Only show typing indicator if it's from the other user (not current user)
        const typingUserId = data.userId?.toString() || data.userId;
        const userId = currentUserId.toString();
        
        if (typingUserId && typingUserId !== userId && typingUserId !== 'current-user') {
          if (data.isTyping) {
            setOtherUserTyping(true);
            // Auto-stop typing indicator after 3 seconds if no update
            if (typingStopTimeoutRef.current) {
              clearTimeout(typingStopTimeoutRef.current);
            }
            typingStopTimeoutRef.current = setTimeout(() => {
              setOtherUserTyping(false);
            }, 3000);
          } else {
            setOtherUserTyping(false);
            if (typingStopTimeoutRef.current) {
              clearTimeout(typingStopTimeoutRef.current);
              typingStopTimeoutRef.current = null;
            }
          }
        }
      }
    };

    onMessage('typing', handleTypingUpdate);

    return () => {
      offMessage('typing');
      if (typingStopTimeoutRef.current) {
        clearTimeout(typingStopTimeoutRef.current);
        typingStopTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, activeConversation?.id, currentUserId, onMessage, offMessage]); // Only depend on activeConversation.id, not entire object

  // Clean up typing state on disconnect
  useEffect(() => {
    if (!isConnected) {
      setIsTyping(false);
      setOtherUserTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      if (typingStopTimeoutRef.current) {
        clearTimeout(typingStopTimeoutRef.current);
        typingStopTimeoutRef.current = null;
      }
    }
  }, [isConnected]);

  const handleSendMessage = async (content: string) => {
    if (!activeConversation || !content.trim()) return;

    const messageInput = {
      content: content.trim(),
      type: 'text' as const
    };

    const result = await sendMessage(messageInput, activeConversation.id);
    
    if (result.success) {
      // Clear typing indicator
      setIsTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  };

  const handleTyping = (content: string) => {
    if (!activeConversation || !isConnected || !currentUserId) return;

    // Clear previous stop timeout
    if (typingStopTimeoutRef.current) {
      clearTimeout(typingStopTimeoutRef.current);
      typingStopTimeoutRef.current = null;
    }

    // Send typing:start if user started typing
    if (content.length > 0 && !isTyping) {
      setIsTyping(true);
      sendTypingIndicator({
        conversationId: activeConversation.id,
        userId: currentUserId,
        isTyping: true,
        timestamp: new Date().toISOString()
      });
    }

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing indicator after 3 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(prev => {
        if (prev) {
          sendTypingIndicator({
            conversationId: activeConversation.id,
            userId: currentUserId,
            isTyping: false,
            timestamp: new Date().toISOString()
          });
        }
        return false;
      });
    }, 3000); // 3 seconds as per plan
  };

  if (!activeConversation) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">💬</div>
          <div className="text-coffee text-xl mb-2">No conversation selected</div>
          <div className="text-coffee/70 text-sm">
            Choose a conversation from the list to start chatting
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Chat Header */}
      <div className="p-4 border-b-2 border-coffee bg-paper">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {onBack && (
              <button
                onClick={onBack}
                className="text-coffee hover:text-terracotta"
              >
                ← Back
              </button>
            )}
            <div className="relative">
              <Avatar
                profilePictureUrl={activeConversation.participant.profilePicture ?? null}
                firstName={activeConversation.participant.firstName}
                lastName={activeConversation.participant.lastName}
                userId={activeConversation.participant.id}
                size="small"
              />
              {activeConversation.participant.isOnline && (
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-cream"></div>
              )}
            </div>
            <div>
              <div className="text-coffee font-bold">
                {activeConversation.participant.firstName} {activeConversation.participant.lastName}
              </div>
              <div className="text-coffee/70 text-sm">
                {activeConversation.participant.isOnline ? 'Online' : 'Offline'}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-coffee/70 text-xs">
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-paper">
        {loading && messages.length === 0 ? (
          <div className="text-center text-coffee/70">Loading messages...</div>
        ) : messages.length > 0 ? (
          messages.map((message) => {
            // Determine if message is from current user
            const msgSenderId = message.senderId?.toString() || message.senderId;
            const userId = currentUserId.toString();
            const isOwnMessage = msgSenderId === userId || msgSenderId === 'current-user';
            
            return (
              <MessageBubble
                key={message.id}
                message={message}
                isOwnMessage={isOwnMessage}
              />
            );
          })
        ) : (
          <div className="text-center text-coffee/70">
            No messages yet. Start the conversation!
          </div>
        )}
        
        {/* Typing Indicators */}
        {isTyping && (
          <div className="flex items-center space-x-2 text-coffee/70 text-sm">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-coffee/40 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-coffee/40 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-coffee/40 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
            <span>You are typing...</span>
          </div>
        )}
        {otherUserTyping && !isTyping && (
          <div className="flex items-center space-x-2 text-coffee/70 text-sm">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-coffee/40 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-coffee/40 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-coffee/40 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
            <span>{activeConversation.participant.firstName} is typing...</span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-4 border-t-2 border-coffee" style={{ backgroundColor: '#F2EFE5' }}>
        <MessageInput
          onSendMessage={handleSendMessage}
          onTyping={handleTyping}
          disabled={!isConnected}
        />
      </div>
    </div>
  );
};

export default ChatWindow;